import { useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { useAccount } from '../../context/AccountContext';
import {
  GET_TINYCMS_DATASET,
  PUBLISH_TINYCMS_CONTENT,
  RUN_TINYCMS_COPY_EDIT,
  UNPUBLISH_TINYCMS_CONTENT,
} from '../../graphql';
import type { Finding, ItemState } from '../cms/shared';
import { Loader } from '../common/PageLoader';
import { askInk } from '../cms/InkConfirm';
import { useAddons } from '../cms/addons';
import { track } from '../../lib/usage';
import { CHECKS_FOLD } from '../cms/pageChecks';
import { openAskAgent } from '../cms/askAgentBus';
import { useSavedNumbers } from '../cms/savedNumbers';
import { HistoryLink } from '../cms/HistoryView';
import { ARTICLE_STATUS } from '../statusWords';
import { isEmptyBullets, suggestionText } from './copyEditQuote';

/**
 * The tinycms half of the article screen ("tinycms UX optimization review
 * v10.zip", Prototype v7 → Article): the publish card, checks, search &
 * sharing, dataset, Reach and approval history. Every rule — an approval names
 * the exact version, blocking findings refuse, a later edit stays off the live
 * site — is the server's; these only show its state and send the click.
 */

export interface CmsArticleState extends ItemState {
  preflight?: Finding[];
  copyEdit?: { fresh: boolean; checkedAt?: string; findings: Finding[]; required?: boolean };
}

export interface CmsArticleContent {
  title: string;
  slug: string;
  summary: string | null;
  hero: { url: string; alt: string | null } | null;
  author: string | null;
  /** The byline (Prototype v17): author ids, the author first, then co-authors. */
  authorIds?: string[];
  /** Show "Updated <date>" beside the published date once a later version is live. */
  showUpdated?: boolean;
  tags: string[];
  /** Site collections it belongs to, by slug. */
  collections?: string[];
  /** The primary one: the article lives at /<section>/<slug>. */
  section?: string | null;
  seo: {
    metaTitle: string | null;
    metaDescription: string | null;
    canonicalUrl: string | null;
    ogImageUrl: string | null;
    noindex: boolean | null;
  };
}

export interface CmsArticle {
  id: string;
  /** Who proposed the working version (GET_TINYCMS_ARTICLE only). */
  proposedBy?: { kind: 'person' | 'feed' | 'agent'; name: string | null } | null;
  /** Waiting on a yes, as the Website list and Today count it (GET_TINYCMS_ARTICLE only). */
  awaitingApproval?: boolean | null;
  slug: string;
  title: string;
  workingHash: string;
  state: CmsArticleState;
  content: CmsArticleContent;
  dataset: {
    name: string | null;
    recordCount: number | null;
    workingHash: string;
    hasPublishedVersion: boolean;
  } | null;
}

export const META_TITLE_MAX = 60;
export const META_DESC_MAX = 160;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const isValidSlug = (slug: string) => SLUG_RE.test(slug) && slug.length <= 80;
export const isHttpUrl = (value: string) => {
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
};

const plain = (html?: string | null) =>
  (html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const clip = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

const when = (iso?: string | null) => (iso ? format(new Date(iso), 'MMM d, yyyy · h:mm a') : '');

/**
 * Status line for the byline and the publish card. awaitingApproval is the
 * server's one rule, so this reads as the Website list and Today do. What's left
 * of "unpublished changes" is an edit sent back with Ask for changes: not waiting.
 */
export function cmsStatus(
  state: CmsArticleState,
  awaitingApproval?: boolean | null
): { label: string; color: string } {
  const changes = Boolean(state.live) && state.hasUnpublishedChanges;
  if (state.scheduled) return { label: ARTICLE_STATUS.scheduled, color: 'var(--ink-accent)' };
  if (state.takenDown && !state.live) return { label: 'Taken down', color: 'var(--ink-ink3)' };
  if (awaitingApproval) return { label: ARTICLE_STATUS.review, color: 'var(--ink-amber)' };
  if (changes)
    return { label: `${ARTICLE_STATUS.live} · unpublished changes`, color: 'var(--ink-amber)' };
  if (state.live) return { label: ARTICLE_STATUS.live, color: 'var(--ink-green)' };
  return { label: ARTICLE_STATUS.draft, color: 'var(--ink-ink3)' };
}

interface CheckRow {
  icon: string;
  text: string;
  sev: string;
  color: string;
  /** A copy edit's optional wording change: listed, but not counted as a problem. */
  suggestion?: boolean;
  /** Only an owner or admin can approve it anyway. */
  held?: boolean;
  /** A copy edit finding: clicking its row goes to the text it quotes. */
  finding?: Finding;
}

export { CHECKS_FOLD };

function findingText(f: Finding) {
  if (isEmptyBullets(f)) return f.label || f.message || 'Empty bullets';
  if (f.quote) {
    const text = suggestionText(f);
    return `“${f.quote}”${text ? ` → ${text}` : ''}${f.message ? ` · ${f.message}` : ''}`;
  }
  // The server's plain words first: a finding's message is written for the agent.
  return f.label || f.message || f.code.replace(/_/g, ' ');
}

function findingRow(f: Finding, severity = f.severity): CheckRow {
  if (f.held) {
    return { icon: '✕', text: findingText(f), sev: 'Held', color: 'var(--ink-red)', held: true };
  }
  // Empty bullets come from preflight: the one finding from there with a place to go.
  const finding = (f.source === 'copy_edit' && f.quote) || isEmptyBullets(f) ? { finding: f } : {};
  return severity === 'blocking'
    ? { icon: '✕', text: findingText(f), sev: 'Blocking', color: 'var(--ink-red)', ...finding }
    : { icon: '●', text: findingText(f), sev: 'Warning', color: 'var(--ink-amber)', ...finding };
}

/**
 * The row after Copy edit, as posts have it (backlog 1c4851c2): the first voice
 * finding. A style-guide finding is already in the list above, and nothing on
 * voice folds into the one line of what passed.
 */
function voiceRow(ce: NonNullable<CmsArticleState['copyEdit']>): CheckRow[] {
  if (!ce.fresh) return [];
  const voice = ce.findings.find((f) => f.code === 'copy_edit_voice');
  return voice ? [{ ...findingRow(voice, 'warning'), text: `Voice: ${findingText(voice)}` }] : [];
}

/** Structure (preflight) and copy-edit results as one list, plus the blocking count. */
export function articleChecks(state: Pick<CmsArticleState, 'preflight' | 'copyEdit'>): {
  rows: CheckRow[];
  blocking: number;
} {
  const preflight = state.preflight || [];
  const ce = state.copyEdit || { fresh: false, findings: [] };
  const required = ce.required !== false;
  const rows: CheckRow[] = [];

  if (!ce.fresh) {
    rows.push(
      required
        ? {
            icon: '✕',
            text: 'Copy edit hasn’t run on this version',
            sev: 'Blocking',
            color: 'var(--ink-red)',
          }
        : {
            icon: '●',
            text: 'Copy edit hasn’t run on this version',
            sev: 'Optional',
            color: 'var(--ink-ink3)',
          }
    );
  } else if (ce.findings.length) {
    // An advisory copy edit never blocks: its findings read as warnings.
    // Its warnings are wording suggestions, counted apart as Today counts them.
    const firstVoice = ce.findings.find((f) => f.code === 'copy_edit_voice');
    const found = ce.findings
      .filter((f) => f !== firstVoice)
      .map((f) => {
        const row = findingRow(f, required ? f.severity : 'warning');
        return row.sev === 'Blocking' ? row : { ...row, suggestion: true };
      });
    rows.push(...found);
  }
  rows.push(...voiceRow(ce));

  for (const f of [...preflight].sort(
    (a, b) =>
      Number(Boolean(b.held)) - Number(Boolean(a.held)) ||
      Number(b.severity === 'blocking') - Number(a.severity === 'blocking')
  )) {
    rows.push(findingRow(f));
  }

  const blocking =
    preflight.filter((f) => f.severity === 'blocking').length +
    (required && ce.fresh ? ce.findings.filter((f) => f.severity === 'blocking').length : 0) +
    (required && !ce.fresh ? 1 : 0);
  // What's held leads: it's what stops the item going live.
  rows.sort((a, b) => Number(Boolean(b.held)) - Number(Boolean(a.held)));
  return { rows, blocking };
}

const QUARTER = 15 * 60 * 1000;
const nextQuarter = () => new Date(Math.ceil((Date.now() + 60 * 1000) / QUARTER) * QUARTER);
const toLocalInput = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm");

/**
 * Status, version, Publish / Schedule / Unpublish. Clicking Publish is the
 * approval of the working version this screen shows (its hash); the server
 * refuses if it changed since, or if blocking checks remain without a reason.
 *
 * Prototype v7 (article detail): this is the one place to approve. When the
 * waiting version is the agent's (agentAsk), it reads
 * "Approve & publish", and any open check sits right beside the button, so the
 * decision and the warning are together. Preview is the header's button only.
 */
const scrollToChecks = () =>
  document
    .querySelector('[data-part="checks"]')
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

/** "2 must fix · 1 suggestion". */
export const checkCounts = (blocking: number, warnings: number) =>
  [
    blocking ? `${blocking} must fix` : '',
    warnings ? `${warnings} ${warnings === 1 ? 'suggestion' : 'suggestions'}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

export function CmsPublishCard({
  article,
  editing,
  disabled,
  onChanged,
  onRequestChanges,
  onDelete,
  deleting,
  agentAsk,
  onOpenHistory,
}: {
  article: CmsArticle;
  editing: boolean;
  disabled?: boolean;
  onChanged: () => void | Promise<unknown>;
  /** Send the agent's version back with notes (the page's Ask agent box). */
  onRequestChanges?: () => void;
  /** Delete the article; it asks first. While it runs, nothing here can. */
  onDelete: () => void;
  deleting: boolean;
  /** The agent made the version waiting here ({ name }: its connector's label, or "Your agent"). */
  agentAsk?: { name: string } | null;
  onOpenHistory?: () => void;
}) {
  const { accountId } = useAccount();
  const [publish, { loading: publishing }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [unpublish, { loading: unpublishing }] = useMutation(UNPUBLISH_TINYCMS_CONTENT);
  const [override, setOverride] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const s = article.state;
  // History's numbers, not the publish count (savedNumbers.ts).
  const numbers = useSavedNumbers(
    'article',
    article.id,
    `${article.workingHash}:${s.live?.version}:${s.scheduled?.version}`
  );
  const live = numbers.of(s.live?.version);
  const scheduledV = numbers.of(s.scheduled?.version);
  const changes = Boolean(s.live) && s.hasUnpublishedChanges;
  const asked = Boolean(agentAsk) && s.hasUnpublishedChanges && !s.scheduled;
  const status = cmsStatus(s, article.awaitingApproval);
  const { rows: checkRows, blocking } = articleChecks(s);
  // Open checks, shown beside the approve button (the full list is under Checks).
  const openBlocking = checkRows.filter((r) => r.sev === 'Blocking' || r.held);
  // Problems only, as Today's card counts them (backlog 94c67434): the copy
  // edit's wording suggestions stay under Checks.
  const openWarnings = checkRows.filter((r) => r.sev === 'Warning' && !r.suggestion);
  const canPublish = s.hasUnpublishedChanges || Boolean(s.scheduled);
  const busy = publishing || unpublishing || deleting || Boolean(disabled);
  // Held: only owners and admins can approve it anyway; nobody else gets the override.
  const hasHeld = checkRows.some((r) => r.held);
  const { isOwner } = useAddons();
  const needsOverride = blocking > 0 && canPublish;
  const canOverride = !hasHeld || isOwner;

  const liveWords = live ? `v${live} live` : 'live';
  const versionLine = s.scheduled
    ? `${scheduledV ? `v${scheduledV} ` : ''}scheduled${s.live ? ` · ${liveWords}` : ''}`
    : s.live
      ? liveWords
      : 'never published';
  const pubNote = editing
    ? 'Finish editing to publish this version.'
    : asked
      ? `${agentAsk!.name} is asking you to approve this exact version.`
      : s.scheduled
        ? `Goes live ${when(s.scheduled.at)}.`
        : changes
          ? `Your saved edits aren’t live yet. Readers still see ${live ? `v${live}` : 'the live version'}.`
          : s.live
            ? 'Readers see the latest version.'
            : 'Not published yet. Blocking checks must pass first.';
  const publishLabel = publishing
    ? 'Publishing…'
    : s.scheduled
      ? 'Publish now'
      : !s.hasUnpublishedChanges
        ? 'Up to date'
        : asked
          ? 'Approve & publish'
          : live
            ? 'Publish changes'
            : 'Publish';

  const run = async (action: 'publish' | 'schedule') => {
    setNote(null);
    const scheduledFor = action === 'schedule' ? new Date(scheduleAt).toISOString() : undefined;
    const ask =
      action === 'publish'
        ? 'Publish this version now?'
        : `Schedule this version for ${when(scheduledFor)}?`;
    if (
      !(await askInk({
        title: ask,
        body: `Version ${article.workingHash.slice(0, 10)}. Your approval is recorded with your name and the time.`,
        confirmLabel: action === 'publish' ? 'Publish' : 'Schedule',
      }))
    ) {
      return;
    }
    try {
      if (hasHeld && override.trim()) track('held_override', { label: 'admin' });
      const { data } = await publish({
        variables: {
          accountId,
          kind: 'article',
          id: article.id,
          action,
          contentHash: article.workingHash,
          scheduledFor,
          overrideReason: override.trim() || undefined,
        },
      });
      const r = data?.publishTinycmsContent;
      if (r?.ok && r.unchanged) setNote({ tone: 'ok', text: 'Already live. Nothing to publish.' });
      else if (r?.ok) {
        const name = await numbers.nameOf(r.version);
        setNote({
          tone: 'ok',
          text:
            action === 'publish'
              ? `Published ${name}.`
              : `Scheduled ${name} for ${when(scheduledFor)}.`,
        });
        setOverride('');
        setScheduleOpen(false);
      } else {
        setNote({
          tone: 'error',
          text: `Refused: ${r?.message || r?.refused || 'unknown reason'}`,
        });
      }
      await onChanged();
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : 'Publish failed' });
    }
  };

  const takeDown = async () => {
    const reason = window.prompt('Take this off the live site? Reason (optional):');
    if (reason === null) return;
    setNote(null);
    try {
      const { data } = await unpublish({
        variables: { accountId, kind: 'article', id: article.id, reason: reason || undefined },
      });
      const r = data?.unpublishTinycmsContent;
      setNote({
        tone: 'ok',
        text: r?.unchanged ? 'Nothing was live.' : 'Unpublished. Readers no longer see it.',
      });
      await onChanged();
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : 'Unpublish failed' });
    }
  };

  return (
    <div className="ink-rail-card">
      <div className="ink-rail-status" style={{ color: status.color }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span className="ink-dot" style={{ background: status.color }} />
          {status.label}
        </span>
        <span className="ink-rail-kicker" style={{ whiteSpace: 'nowrap' }}>
          {versionLine}
          {onOpenHistory ? (
            <>
              {' · '}
              <HistoryLink onOpen={onOpenHistory} disabled={editing} />
            </>
          ) : null}
        </span>
      </div>
      <div className="ink-rail-note">{pubNote}</div>
      {canPublish && !editing && (openBlocking.length || openWarnings.length) ? (
        <div className="ink-pub-warn" data-tone={openBlocking.length ? 'blocking' : 'warning'}>
          <span className="ink-dot" />
          {/* Counts only: each problem is listed once, under Checks (backlog b23a8dd3). */}
          <button type="button" className="ink-pub-warn-link" onClick={scrollToChecks}>
            {checkCounts(openBlocking.length, openWarnings.length)}
          </button>
        </div>
      ) : null}
      {needsOverride && !editing && canOverride ? (
        <>
          <input
            className="ink-input ink-override"
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder={hasHeld ? 'Approve anyway, with a reason' : 'Override reason'}
            aria-label={hasHeld ? 'Approve anyway, with a reason' : 'Override reason'}
            aria-describedby="override-reason-help"
          />
          <span
            id="override-reason-help"
            className="ink-form-hint"
            style={{ display: 'block', marginTop: 4 }}
          >
            {hasHeld
              ? 'Only owners and admins can approve held items. It’s recorded with your approval.'
              : 'A reason is needed to approve with blocking checks. It’s recorded with your approval.'}
          </span>
        </>
      ) : null}
      {needsOverride && !editing && !canOverride ? (
        <span className="ink-held-note">Only owners and admins can approve held items.</span>
      ) : null}
      <div className="ink-rail-actions">
        <button
          type="button"
          className={`ink-btn ${canPublish ? 'ink-btn-primary' : ''}`}
          onClick={() => void run('publish')}
          disabled={
            busy || editing || !canPublish || (needsOverride && (!canOverride || !override.trim()))
          }
        >
          {publishLabel}
        </button>
        <button
          type="button"
          className="ink-btn"
          style={{ flex: 'none' }}
          onClick={() => {
            setScheduleOpen((o) => !o);
            if (!scheduleAt) setScheduleAt(toLocalInput(nextQuarter()));
          }}
          disabled={busy || editing}
        >
          Schedule
        </button>
      </div>
      {scheduleOpen && !editing ? (
        <div style={{ marginTop: 12 }}>
          <input
            type="datetime-local"
            step={900}
            className="ink-input"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            aria-label="Publish at"
          />
          <div className="ink-rail-actions">
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => void run('schedule')}
              disabled={
                busy ||
                !scheduleAt ||
                new Date(scheduleAt).getTime() <= Date.now() ||
                (blocking > 0 && (!canOverride || !override.trim()))
              }
            >
              {publishing ? 'Scheduling…' : 'Confirm'}
            </button>
            <button type="button" className="ink-btn" onClick={() => setScheduleOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {note ? (
        <div
          className="ink-rail-note"
          role="status"
          style={{ color: note.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green-ink)' }}
        >
          {note.text}
        </div>
      ) : null}
      {/* Request changes · Delete · Unpublish: sending an agent's version back
          and deleting it are the usual alternatives to approving it (owner,
          2026-11-01; Delete was at the foot of the panel and easy to miss). */}
      {!editing || s.live || s.scheduled ? (
        <div className="ink-rail-links">
          {asked && onRequestChanges && !editing ? (
            <button
              type="button"
              className="ink-link-btn"
              style={{ color: 'var(--ink-ink2)' }}
              onClick={onRequestChanges}
              disabled={busy}
            >
              Request changes
            </button>
          ) : null}
          {!editing ? (
            <button
              type="button"
              className="ink-link-btn ink-rail-delete"
              onClick={onDelete}
              disabled={busy}
            >
              {deleting ? 'Deleting…' : s.live || s.scheduled ? 'Delete article' : 'Delete draft'}
            </button>
          ) : null}
          {s.live || s.scheduled ? (
            <button
              type="button"
              className="ink-link-btn"
              style={{ color: 'var(--ink-ink3)' }}
              onClick={() => void takeDown()}
              disabled={busy}
            >
              {unpublishing ? '…' : 'Unpublish'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export const sameFinding = (a: Finding, b: Finding) =>
  a.path === b.path && a.quote === b.quote && a.suggestion === b.suggestion;

/**
 * The finding after this one, among those like it: suggestions are listed apart
 * and may be folded away, so going through the problems never lands on one.
 */
export function nextFinding(
  rows: CheckRow[],
  current: Finding,
  usable: (f: Finding) => boolean = () => true
): Finding | null {
  const here = rows.find((r) => r.finding && sameFinding(r.finding, current));
  if (!here) return null;
  const like = rows.filter((r) => r.finding && Boolean(r.suggestion) === Boolean(here.suggestion));
  const after = like.slice(like.indexOf(here) + 1);
  return after.find((r) => usable(r.finding as Finding))?.finding ?? null;
}

/** One line under Checks; a copy edit finding's goes to its text. */
export function CheckRowItem({
  row: c,
  onOpenFinding,
}: {
  row: CheckRow;
  onOpenFinding?: (f: Finding) => void;
}) {
  const row = (
    <>
      <span className="ink-check-icon" style={{ color: c.color }}>
        {c.icon}
      </span>
      <span>{c.text}</span>
      <span className="ink-check-sev" style={{ color: c.suggestion ? 'var(--ink-ink3)' : c.color }}>
        {c.suggestion ? 'Suggestion' : c.sev}
      </span>
    </>
  );
  const { finding } = c;
  return finding && onOpenFinding ? (
    <button
      type="button"
      className="ink-check ink-check-go"
      title="Go to this text"
      onClick={() => onOpenFinding(finding)}
    >
      {row}
    </button>
  ) : (
    <div className="ink-check">{row}</div>
  );
}

/** Structure and copy-edit results, with Run copy edit. */
export function ArticleChecks({
  article,
  editing,
  onChanged,
  edit,
  onOpenFinding,
}: {
  article: CmsArticle;
  editing: boolean;
  onChanged: () => void | Promise<unknown>;
  /** While editing: the checks on the text on screen, and the check that reads it. */
  edit?: {
    state: CmsArticleState;
    /** The text changed after the check these findings came from. */
    stale: boolean;
    run: () => Promise<unknown>;
  };
  onOpenFinding?: (f: Finding) => void;
}) {
  const { accountId } = useAccount();
  const [copyEdit, { loading: savedLoading }] = useMutation(RUN_TINYCMS_COPY_EDIT);
  const [draftLoading, setDraftLoading] = useState(false);
  const loading = savedLoading || draftLoading;
  const [error, setError] = useState('');
  const { rows } = articleChecks(editing && edit ? edit.state : article.state);
  // Wording suggestions as one count, opened on request (backlog cf841811):
  // a column of amber Warnings read as problems with a published article.
  const [showSuggestions, setShowSuggestions] = useState(false);
  const problems = rows.filter((r) => !r.suggestion);
  const suggestions = rows.filter((r) => r.suggestion);
  const heldRows = rows.filter((r) => r.held);

  const runCopyEdit = async () => {
    setError('');
    try {
      if (editing && edit) {
        setDraftLoading(true);
        await edit.run();
      } else {
        await copyEdit({ variables: { accountId, id: article.id } });
        await onChanged();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Copy edit failed');
    } finally {
      setDraftLoading(false);
    }
  };

  return (
    <section data-part="checks">
      <div className="ink-rail-head">
        <div className="ink-rail-label" style={{ margin: 0 }}>
          Checks
        </div>
        <button
          type="button"
          className="ink-link-btn"
          onClick={() => void runCopyEdit()}
          disabled={loading || (editing && !edit)}
          title={editing && !edit ? 'Finish editing first' : undefined}
        >
          {loading ? 'Checking…' : 'Run copy edit'}
        </button>
      </div>
      {editing && edit?.stale ? (
        <div className="ink-rail-note">You’ve edited since this check.</div>
      ) : null}
      {[...problems, ...(showSuggestions ? suggestions : [])].map((c, i) => (
        <CheckRowItem row={c} key={i} onOpenFinding={onOpenFinding} />
      ))}
      {heldRows.length ? (
        <button
          type="button"
          className="ink-link-btn"
          onClick={() =>
            openAskAgent({
              action: 'healthFix',
              ctx: {
                title: article.title,
                fix: (article.state.preflight || [])
                  .filter((f) => f.held)
                  .map((f) => f.message || f.label)
                  .join(' '),
              },
            })
          }
        >
          Ask your agent to fix
        </button>
      ) : null}
      {suggestions.length ? (
        <button
          type="button"
          className="ink-check ink-check-more"
          onClick={() => setShowSuggestions((v) => !v)}
          aria-expanded={showSuggestions}
        >
          <span className="ink-check-icon" style={{ color: 'var(--ink-ink3)' }}>
            ●
          </span>
          <span>
            {suggestions.length} wording {suggestions.length === 1 ? 'suggestion' : 'suggestions'}
          </span>
          <span className="ink-check-sev" style={{ color: 'var(--ink-ink3)' }}>
            {showSuggestions ? 'Hide' : 'Show'}
          </span>
        </button>
      ) : null}
      <div className="ink-check ink-check-fold">
        <span className="ink-check-icon" style={{ color: 'var(--ink-green)' }}>
          ✓
        </span>
        <span>{CHECKS_FOLD}</span>
      </div>
      {error ? (
        <div className="ink-rail-note" style={{ color: 'var(--ink-red)' }}>
          {error}
        </div>
      ) : null}
    </section>
  );
}

export interface SeoDraft {
  slug: string;
  /** The primary collection's slug, '' for the site's article address. */
  section: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  ogImageUrl: string;
}

/** A search result and share card, read-only; or the fields behind them. */
export function SearchSharing({
  article,
  editing,
  draft,
  onDraft,
  title,
  summary,
  heroUrl,
  siteHost,
  addressing,
  onToggleNoindex,
  togglingNoindex,
  structuredData = null,
}: {
  article: CmsArticle;
  editing: boolean;
  draft: SeoDraft;
  onDraft: (patch: Partial<SeoDraft>) => void;
  /** The title and summary on screen (the defaults for meta title / description). */
  title: string;
  summary: string;
  heroUrl: string;
  /** The site's address, no scheme: "acme.tinycms.ai". */
  siteHost: string;
  /**
   * Where articles live (site document): `base`, the site's article address
   * ("article" unless set, e.g. "news"), and the collections an article can
   * live under instead.
   */
  addressing: { base: string; sections: Array<{ slug: string; name: string }> };
  onToggleNoindex: () => void;
  togglingNoindex: boolean;
  /** What search engines read about who wrote it (Prototype v17), amber when no one. */
  structuredData?: { text: string; warn: boolean } | null;
}) {
  const seo = article.content.seo || ({} as CmsArticleContent['seo']);
  const noindex = Boolean(seo.noindex);
  const slug = editing ? draft.slug : article.slug;
  // The address prefix: its collection while the site has it, else the site's.
  const section = editing ? draft.section : article.content.section || '';
  const prefix = addressing.sections.some((c) => c.slug === section) ? section : addressing.base;
  const siteBase = `${siteHost}/${prefix}/`;
  const movesLive =
    editing &&
    article.state.live &&
    (draft.slug !== article.slug || draft.section !== (article.content.section || ''));
  const metaTitle = (editing ? draft.metaTitle : seo.metaTitle) || title;
  const metaDesc = (editing ? draft.metaDescription : seo.metaDescription) || plain(summary);
  const shareImage = (editing ? draft.ogImageUrl : seo.ogImageUrl) || heroUrl;
  const canonical = editing ? draft.canonicalUrl : seo.canonicalUrl;
  const slugBad = editing && !isValidSlug(draft.slug);
  const canonicalBad = editing && Boolean(draft.canonicalUrl) && !isHttpUrl(draft.canonicalUrl);
  const ogBad = editing && Boolean(draft.ogImageUrl) && !isHttpUrl(draft.ogImageUrl);

  return (
    <section data-part="seo" className="ink-detail-block">
      <div className="ink-detail-block-head">
        <h3>Search &amp; sharing</h3>
        <button
          type="button"
          className="ink-noindex"
          role="switch"
          aria-checked={noindex}
          onClick={onToggleNoindex}
          disabled={togglingNoindex}
        >
          Hide from search engines
          <span className="ink-mini-switch" data-on={noindex ? 'true' : 'false'} />
        </button>
      </div>
      {noindex ? (
        <p className="ink-form-hint" style={{ marginTop: 6 }}>
          Once published, search engines are asked not to list this article.
        </p>
      ) : null}

      {!editing ? (
        <>
          <div className="ink-serp">
            <div style={{ minWidth: 0 }}>
              <div className="ink-serp-url">
                {siteBase}
                {slug}
              </div>
              <div className="ink-serp-title">{clip(metaTitle, META_TITLE_MAX)}</div>
              <div className="ink-serp-desc">
                {clip(metaDesc, META_DESC_MAX) || 'No description.'}
              </div>
            </div>
            <div className="ink-serp-img">
              {shareImage ? <img src={shareImage} alt="" /> : <span>share image</span>}
            </div>
          </div>
          <div className="ink-serp-foot">
            <span>Canonical: {canonical || 'same as URL'}</span>
            <span>
              Share image:{' '}
              {seo.ogImageUrl ? 'custom' : heroUrl ? 'uses hero' : 'none (add a hero image)'}
            </span>
          </div>
          {structuredData ? (
            <div className="ink-serp-sd" data-warn={structuredData.warn ? 'true' : undefined}>
              {structuredData.text}
            </div>
          ) : null}
        </>
      ) : (
        <div className="ink-form-grid">
          <label className="ink-form-full">
            <span className="ink-form-label">URL slug</span>
            <div className="ink-slug" data-bad={slugBad ? 'true' : undefined}>
              <span>{siteBase}</span>
              <input
                value={draft.slug}
                onChange={(e) => onDraft({ slug: e.target.value.toLowerCase() })}
                placeholder="my-article"
                aria-label="URL slug"
                spellCheck={false}
              />
            </div>
            {slugBad ? (
              <span className="ink-form-hint" style={{ color: 'var(--ink-red)' }}>
                Lowercase letters and digits, separated by single hyphens.
              </span>
            ) : movesLive ? (
              <span className="ink-form-hint">
                The old address will redirect here once you publish.
              </span>
            ) : null}
          </label>
          {addressing.sections.length ? (
            <label className="ink-form-full">
              <span className="ink-form-label">Lives under</span>
              <select
                className="ink-input"
                value={
                  addressing.sections.some((c) => c.slug === draft.section) ? draft.section : ''
                }
                onChange={(e) => onDraft({ section: e.target.value })}
                aria-label="Lives under"
              >
                <option value="">/{addressing.base}/ (all articles)</option>
                {addressing.sections.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    /{c.slug}/ ({c.name})
                  </option>
                ))}
              </select>
              <span className="ink-form-hint">
                Its primary collection. Choosing one also adds the article to it.
              </span>
            </label>
          ) : null}
          <label>
            <span className="ink-form-label">
              Meta title
              <span
                className="ink-form-count"
                data-over={metaTitle.length > META_TITLE_MAX ? 'true' : undefined}
              >
                {metaTitle.length} / {META_TITLE_MAX}
              </span>
            </span>
            <input
              className="ink-input"
              data-ce-field="seo.metaTitle"
              value={draft.metaTitle}
              placeholder="Meta title"
              onChange={(e) => onDraft({ metaTitle: e.target.value })}
            />
            <span className="ink-form-hint">Blank uses the title</span>
          </label>
          <label>
            <span className="ink-form-label">Canonical URL</span>
            <input
              className="ink-input"
              value={draft.canonicalUrl}
              placeholder="https://example.com/article"
              onChange={(e) => onDraft({ canonicalUrl: e.target.value.trim() })}
              data-bad={canonicalBad ? 'true' : undefined}
            />
            <span className="ink-form-hint">Blank uses this article’s address</span>
          </label>
          <label className="ink-form-full">
            <span className="ink-form-label">
              Meta description
              <span
                className="ink-form-count"
                data-over={metaDesc.length > META_DESC_MAX ? 'true' : undefined}
              >
                {metaDesc.length} / {META_DESC_MAX}
              </span>
            </span>
            <textarea
              className="ink-input"
              rows={2}
              data-ce-field="seo.metaDescription"
              value={draft.metaDescription}
              placeholder="Meta description"
              onChange={(e) => onDraft({ metaDescription: e.target.value })}
            />
            <span className="ink-form-hint">Blank uses the summary</span>
          </label>
          <label className="ink-form-full">
            <span className="ink-form-label">Share image URL</span>
            <input
              className="ink-input"
              value={draft.ogImageUrl}
              placeholder="https://example.com/photo.jpg"
              onChange={(e) => onDraft({ ogImageUrl: e.target.value.trim() })}
              data-bad={ogBad ? 'true' : undefined}
            />
          </label>
        </div>
      )}
    </section>
  );
}

/**
 * The article's JSON dataset: its schema check, what is live, and the
 * document itself. Agents attach and change it; it publishes on its own.
 */
export function DatasetCard({ article }: { article: CmsArticle }) {
  const { accountId } = useAccount();
  const has = Boolean(article.dataset);
  const { data, loading, refetch } = useQuery(GET_TINYCMS_DATASET, {
    variables: { accountId, articleId: article.id, includeDocument: true },
    skip: !accountId || !has,
    fetchPolicy: 'cache-and-network',
  });
  const [publish, { loading: publishing }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [showAll, setShowAll] = useState(false);
  const ds = data?.tinycmsDataset;
  const summary = article.dataset;
  const records = summary?.recordCount ?? ds?.recordCount;

  const findings: Finding[] = ds?.state?.findings || [];
  const blocking = findings.filter((f) => f.severity === 'blocking').length;
  const json = ds?.document !== undefined ? JSON.stringify(ds.document, null, 2) : '';
  const lines = json.split('\n');
  const long = lines.length > 40;

  const publishDataset = async () => {
    if (!ds) return;
    if (
      !(await askInk({
        title: 'Publish this dataset now?',
        body: `Version ${String(ds.workingHash).slice(0, 10)}. Your approval is recorded with your name and the time.`,
        confirmLabel: 'Publish',
      }))
    ) {
      return;
    }
    setNote(null);
    try {
      const { data: res } = await publish({
        variables: {
          accountId,
          kind: 'dataset',
          id: article.id,
          action: 'publish',
          contentHash: ds.workingHash,
        },
      });
      const r = res?.publishTinycmsContent;
      setNote(
        r?.ok
          ? { tone: 'ok', text: r.unchanged ? 'Already live.' : `Published dataset v${r.version}.` }
          : { tone: 'error', text: `Refused: ${r?.message || r?.refused}` }
      );
      await refetch();
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : 'Publish failed' });
    }
  };

  const liveLabel = ds?.state?.live
    ? ds.state.hasUnpublishedChanges
      ? `v${ds.state.live.version} live · changes not live`
      : `v${ds.state.live.version} live`
    : ds?.state?.scheduled
      ? `v${ds.state.scheduled.version} scheduled`
      : 'not published';

  return (
    <section data-part="dataset" className="ink-detail-block">
      <div className="ink-detail-block-head">
        <h3>Dataset</h3>
        {has ? (
          <span className="ink-form-hint">
            {records != null ? `${records} record${records === 1 ? '' : 's'} · ` : ''}JSON ·
            published separately
          </span>
        ) : null}
      </div>
      {!has ? (
        <p className="ink-detail-empty">
          No dataset. Your agent can attach one: a JSON document with a declared schema.
        </p>
      ) : loading && !ds ? (
        <Loader label="Loading dataset" className="ink-detail-empty" />
      ) : ds ? (
        <div className="ink-ds">
          <div className="ink-ds-head">
            <span style={{ fontFamily: 'var(--ink-mono)' }}>{ds.name || 'dataset'}</span>
            {!ds.state?.checksFresh ? (
              <span style={{ color: 'var(--ink-ink3)' }}>Schema not checked</span>
            ) : findings.length ? (
              <span style={{ color: blocking ? 'var(--ink-red)' : 'var(--ink-amber)' }}>
                {findings.length} schema finding{findings.length === 1 ? '' : 's'}
              </span>
            ) : (
              <span style={{ color: 'var(--ink-green-ink)' }}>✓ Schema check passes</span>
            )}
            {ds.state?.schemaChanged ? (
              <span style={{ color: 'var(--ink-amber)' }}>Schema changed</span>
            ) : null}
            <span style={{ marginLeft: 'auto', color: 'var(--ink-ink3)', fontWeight: 400 }}>
              {liveLabel}
            </span>
            {ds.state?.hasUnpublishedChanges ? (
              <button
                type="button"
                className="ink-link-btn"
                onClick={() => void publishDataset()}
                disabled={publishing || blocking > 0}
                title={
                  blocking ? 'Blocking schema findings: ask your agent to fix them' : undefined
                }
              >
                {publishing ? 'Publishing…' : 'Publish dataset'}
              </button>
            ) : null}
          </div>
          {findings.length ? (
            <ul className="ink-ds-findings">
              {findings.map((f, i) => (
                <li
                  key={i}
                  style={{
                    color: f.severity === 'blocking' ? 'var(--ink-red)' : 'var(--ink-amber)',
                  }}
                >
                  {f.message || f.code}
                  {f.path ? <span style={{ color: 'var(--ink-ink3)' }}> · {f.path}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          <pre className="ink-ds-json">
            {long && !showAll ? `${lines.slice(0, 40).join('\n')}\n…` : json}
          </pre>
          {long ? (
            <button
              type="button"
              className="ink-link-btn"
              style={{ margin: '0 16px 12px' }}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? 'Show less' : `Show all ${lines.length} lines`}
            </button>
          ) : null}
        </div>
      ) : null}
      {has ? (
        <p className="ink-form-hint" style={{ marginTop: 8 }}>
          To change the data, ask your agent.
        </p>
      ) : null}
      {note ? (
        <p
          className="ink-form-hint"
          style={{ color: note.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green-ink)' }}
        >
          {note.text}
        </p>
      ) : null}
    </section>
  );
}

/** Tag chips with remove, and "+ add" (Enter or comma adds). */
export function TagEditor({
  tags,
  onChange,
}: {
  tags: string[];
  onChange: (next: string[]) => void;
}) {
  const [text, setText] = useState('');
  const add = () => {
    const parts = text
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .filter((t) => !tags.includes(t));
    if (parts.length) onChange([...tags, ...parts]);
    setText('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !text && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };
  return (
    <span className="ink-rail-terms">
      {tags.map((t) => (
        <span className="ink-rail-chip" key={t}>
          {t}
          <button
            type="button"
            className="ink-chip-x"
            aria-label={`Remove ${t}`}
            onClick={() => onChange(tags.filter((x) => x !== t))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="ink-tag-input"
        value={text}
        placeholder="Add tag"
        aria-label="Add tag"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        onBlur={add}
      />
    </span>
  );
}
